<!--
  Infos / forcage du montant remboursable AMO d'une prestation.
  Port web de frmInfosCompPresta.vb : ouvert en popup depuis le panneau de
  detail d'acte de NewFSE.vue (bouton « Montant remb. AMO »).

  - Affiche les infos de remboursement de la prestation (lbInfos du legacy).
  - Permet de forcer le montant remboursable AMO (case + montant + pas +/-).
  - Taux d'abattement (0/50/100 %) pour les actes IK (specialites 24/86).
  - Lecture/ecriture directes sur facfse.Prest[backIndex] via src/script/PartAMO.js.
-->
<template>
    <v-card class="infos-amo">
        <v-card-title class="d-flex align-center">
            <span>Montant remboursable AMO — {{ acte.type }} {{ acte.code }}</span>
            <v-spacer></v-spacer>
            <v-btn icon="mdi-close" size="small" variant="text" @click="annuler"></v-btn>
        </v-card-title>

        <v-card-text>
            <div v-if="!prestTrouvee" class="erreurs-amo mb-2">
                Acte non rattache a la facture (saisi localement, non encore
                enregistre). Le forcage AMO sera possible apres enregistrement.
            </div>

            <!-- Bloc infos (lecture seule), equivalent lbInfos -->
            <div class="bloc-infos">
                <div class="ligne-info"><span class="lbl">Base de remboursement</span><span>{{ fmt(infos.baseRemb) }}</span></div>
                <div class="ligne-info"><span class="lbl">Taux de remboursement</span><span>{{ infos.tauxRemb }} %</span></div>
                <div class="ligne-info"><span class="lbl">Majoration TM hors parcours</span><span>{{ fmt(infos.mtm) }}</span></div>

                <template v-if="acte.type === 'CCAM'">
                    <div class="ligne-info"><span class="lbl">Base remb. hors forfait</span><span>{{ fmt(infos.baserembhorsforfait) }}</span></div>
                    <div class="ligne-info"><span class="lbl">Base remb. theorique AMO</span><span>{{ fmt(infos.baserembthAMO) }}</span></div>
                    <div class="ligne-info"><span class="lbl">Montant personnel prestation</span><span>{{ fmt(infos.montantpersonnel) }}</span></div>
                    <div class="ligne-info"><span class="lbl">Prix unitaire prestation</span><span>{{ fmt(infos.prixunitaire) }}</span></div>
                </template>

                <div class="ligne-info"><span class="lbl">Forcages</span><span>{{ infos.forcages.length ? infos.forcages.join(', ') : 'Aucun' }}</span></div>
                <div class="ligne-info"><span class="lbl">Montant remb. AMO calcule</span><span>{{ fmt(infos.partAMOCalc) }}</span></div>
                <div class="ligne-info ligne-amo"><span class="lbl">Montant remb. AMO {{ infos.partAMOForce ? '(force)' : '' }}</span><span>{{ fmt(infos.partAMONew) }}</span></div>
            </div>

            <!-- Forcage du montant remboursable AMO -->
            <div class="bloc-forcage mt-4">
                <v-checkbox v-model="forcer" label="Forcer le montant remboursable AMO"
                            density="compact" hide-details></v-checkbox>

                <div class="d-flex align-center ga-2 mt-2" :class="{ 'zone-desactivee': !forcer }">
                    <div class="champ taille-sm">
                        <label>Montant force</label>
                        <input v-model="montantForce" :disabled="!forcer" class="champ-input"
                               @blur="montantForce = fmtNombre(montantForce)" />
                    </div>
                    <div class="champ taille-xs">
                        <label>Pas</label>
                        <input v-model="pas" :disabled="!forcer" class="champ-input" />
                    </div>
                    <v-btn icon="mdi-minus" size="small" variant="tonal" color="#119393"
                           :disabled="!forcer" @click="incrementer(-1)"></v-btn>
                    <v-btn icon="mdi-plus" size="small" variant="tonal" color="#119393"
                           :disabled="!forcer" @click="incrementer(1)"></v-btn>
                </div>
            </div>

            <!-- Taux d'abattement (actes IK) -->
            <div v-if="abatVisible" class="bloc-abat mt-4">
                <label class="font-weight-bold">Taux d'abattement (acte IK)</label>
                <select v-model.number="tauxAbat" class="champ-input champ-combo mt-1">
                    <option v-for="o in tauxAbatOptions" :key="o.valeur" :value="o.valeur">{{ o.libelle }}</option>
                </select>
            </div>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="annuler">Annuler</v-btn>
            <v-btn color="#119393" :loading="enregistrement" :disabled="!prestTrouvee" @click="valider">Valider</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
    import { defineComponent } from 'vue';
    import {
        TAUX_ABAT_OPTIONS, estAbattementVisible, getPrest,
        infosPartAMO, appliquerPartAMO, arrondi,
    } from '../script/PartAMO.js';

    export default defineComponent({
        // acte : objet local { type, code, backIndex } de NewFSE.vue
        // specialite : code specialite praticien (carte CPS), pour le taux d'abattement
        props: {
            acte: { type: Object, required: true },
            specialite: { type: String, default: '' },
        },
        emits: ['close'],

        data() {
            return {
                tauxAbatOptions: TAUX_ABAT_OPTIONS,
                infos: infosPartAMO(null),
                prestTrouvee: false,
                forcer: false,
                montantForce: '0.00',
                pas: '1.00',
                tauxAbat: 0,
                enregistrement: false,
            };
        },

        computed: {
            abatVisible() {
                return estAbattementVisible(this.acte && this.acte.code, this.specialite);
            },
        },

        methods: {
            fmt(v) { return (Number(v) || 0).toFixed(2) + ' €'; },
            fmtNombre(v) {
                const n = parseFloat(String(v).replace(',', '.'));
                return isNaN(n) ? '0.00' : n.toFixed(2);
            },

            incrementer(sens) {
                const cur = parseFloat(String(this.montantForce).replace(',', '.')) || 0;
                const step = parseFloat(String(this.pas).replace(',', '.')) || 0;
                const next = cur + sens * step;
                if (next >= 0) this.montantForce = next.toFixed(2);
            },

            annuler() { this.$emit('close'); },

            async valider() {
                if (!this.prestTrouvee) return;
                this.enregistrement = true;
                try {
                    const original = this.$store.getters['FSEHandler/getFacfse'];
                    if (!original) return;
                    const facfse = JSON.parse(JSON.stringify(original));
                    const prest = getPrest(facfse, this.acte.backIndex);
                    if (!prest) {
                        console.warn('Prest introuvable pour le forcage AMO :', this.acte.code);
                        return;
                    }
                    appliquerPartAMO(prest, {
                        forcer: this.forcer,
                        montant: this.montantForce,
                        tauxAbat: this.tauxAbat,
                        abatVisible: this.abatVisible,
                    });
                    await this.$store.dispatch('FSEHandler/setFacfse', facfse);
                    this.$emit('close');
                } catch (e) {
                    console.error('Erreur lors du forcage du montant AMO :', e);
                } finally {
                    this.enregistrement = false;
                }
            },
        },

        mounted() {
            const facfse = this.$store.getters['FSEHandler/getFacfse'];
            const prest = getPrest(facfse, this.acte && this.acte.backIndex);
            this.prestTrouvee = !!prest;
            this.infos = infosPartAMO(prest);
            this.forcer = this.infos.partAMOForce;
            // montant initial : valeur forcee si forcee, sinon valeur calculee.
            this.montantForce = (this.infos.partAMOForce ? this.infos.partAMONew : this.infos.partAMOCalc).toFixed(2);
            this.tauxAbat = arrondi(this.infos.tauxAbat) | 0;
        },
    });
</script>

<style scoped>
    * { color: #434343; }

    .infos-amo { padding-bottom: 6px; }

    .bloc-infos {
        background: #f5f5f5;
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 0.9rem;
    }
    .ligne-info {
        display: flex;
        justify-content: space-between;
        padding: 3px 0;
        border-bottom: 1px solid #ececec;
    }
    .ligne-info:last-child { border-bottom: none; }
    .ligne-info .lbl { color: #555; }
    .ligne-amo {
        font-weight: bold;
        border-top: 1px solid #ccc;
        margin-top: 4px;
        padding-top: 6px;
    }

    .champ { display: flex; flex-direction: column; }
    .champ.taille-xs { width: 90px; }
    .champ.taille-sm { width: 150px; }
    .champ label { font-size: 0.8rem; font-weight: bold; color: #555; margin-bottom: 4px; }
    .champ-input {
        padding: 8px 10px;
        border: 1px solid #ccc;
        border-radius: 6px;
        font-size: 0.95rem;
        outline: none;
    }
    .champ-input:focus { border-color: #119393; }
    .champ-combo { height: auto; min-height: 40px; background: #fff; }

    .zone-desactivee { opacity: 0.55; }

    .erreurs-amo {
        color: #E8A33D;
        font-weight: bold;
        font-size: 0.9rem;
        line-height: 1.4;
    }
</style>
